// src/pages/Vendor/VendorForm.js
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useVendors } from "../../context/VendorContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../context/AuthContext";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import NavigationBlocker from "../../components/common/NavigationBlocker";

// 🟢 1. IMPORT CUSTOM FIELD COMPONENTS
import { useCustomFields } from "../../hooks/useCustomFields";
import CustomFieldRenderer from "../../components/common/CustomFieldRenderer";

import { toInputDate } from "../../utils/dateUtils";

const mockProjects = [
  "Project A",
  "Project B",
  "Project C",
  "Bridge Construction",
  "Mall Renovation"
];

const VendorForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addVendor, updateVendor, fetchVendorById, getVendorById } = useVendors();
  const { checkAccess } = useAuth();

  const [isDirty, setIsDirty] = useState(false);
  const loadedIdRef = React.useRef(null);

  useUnsavedChanges(isDirty);

  useEffect(() => {
    if (!checkAccess('vendor.manage')) {
        toast.error("Access Denied: You cannot manage vendors.");
        navigate('/admin/VendorPage');
    }
  }, [checkAccess, navigate]);

  const editing = Boolean(id);

  // 🟢 2. INITIALIZE CUSTOM FIELDS HOOK ('vendors' module)
  const { customData, setCustomData, saveCustomFields } = useCustomFields('vendors', editing ? id : null);

  const [formData, setFormData] = useState({
    vendorName: "",
    companyName: "",
    category: "",
    gstNumber: "",
    panNumber: "",
    licenseNumber: "",
    certificateExpiry: "",
    certificateFile: null,

    contactPerson: "",
    email: "",
    phone: "",
    address: "",

    contractStart: "",
    contractEnd: "",
    daysLeft: "",

    rating: 0,
    performanceEvaluation: "",

    vendorStatus: "Active",
    assignedProjects: [],
  });

  useEffect(() => {
    if (editing && checkAccess('vendor.manage')) {
      if (loadedIdRef.current === String(id)) return;
      loadedIdRef.current = String(id);

      const loadVendor = async () => {
        let existing = null;
        if (fetchVendorById) {
          existing = await fetchVendorById(id);
        }
        if (!existing && getVendorById) {
          existing = getVendorById(id);
        }
        if (existing) {
          setFormData(prev => ({
            ...prev,
            ...existing,
            certificateExpiry: toInputDate(existing.certificateExpiry),
            contractStart: toInputDate(existing.contractStart),
            contractEnd: toInputDate(existing.contractEnd)
          }));
        }
      };
      loadVendor();
    }
  }, [editing, id, fetchVendorById, getVendorById, checkAccess]);

  useEffect(() => {
    if (formData.contractEnd) {
      const today = new Date();
      const end = new Date(formData.contractEnd);
      const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
      setFormData((prev) => ({ ...prev, daysLeft: isNaN(diff) ? "" : diff }));
    }
  }, [formData.contractEnd]);

  const handleChange = (e) => {
    setIsDirty(true);
    const { name, value, files } = e.target;

    if (name === "certificateFile") {
      setFormData((prev) => ({ ...prev, certificateFile: files[0] }));
    } 
    else if (name === "assignedProjects") {
      const selected = Array.from(
        e.target.selectedOptions,
        (option) => option.value
      );
      setFormData((prev) => ({ ...prev, assignedProjects: selected }));
    } 
    else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // 🟢 3. UPDATE SUBMIT TO ASYNC AND MERGE CUSTOM DATA
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      custom_data: customData
    };

    if (editing) {
      await updateVendor(id, payload);
      await saveCustomFields(id); // Save custom fields for existing vendor
      toast.success("Vendor updated successfully!", { theme: "colored" });
    } else {
      const result = await addVendor(payload);
      // Ensure we get the ID properly to save the custom fields
      const newId = result?.id || result?.data?.id || result;
      if (newId) await saveCustomFields(newId);
      toast.success("Vendor added successfully!", { theme: "colored" });
    }

    setIsDirty(false);
    setTimeout(() => navigate("/admin/VendorPage"), 700);
  };

  if (!checkAccess('vendor.manage')) return null;

  return (
    <>
      <div className="p-6 bg-white min-h-screen">
        <h1 className="text-base font-bold mb-6">
          {editing ? "Edit Vendor" : "Add Vendor"}
        </h1>

        <form
          onSubmit={handleSubmit}
          className="max-w-2xl bg-white shadow-md rounded-lg p-6 space-y-6"
        >
          {/* Basic Info */}
          <div>
            <label className="block text-sm font-medium mb-2">Vendor Name</label>
            <input
              type="text"
              name="vendorName"
              value={formData.vendorName}
              onChange={handleChange}
              required
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Company Name</label>
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium mb-2">Category</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
            >
              <option value="">Select Category</option>
              <option value="civil">Civil</option>
              <option value="electrical">Electrical</option>
              <option value="plumbing">Plumbing</option>
              <option value="materials">Materials Supplier</option>
              <option value="machinery">Machinery</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Compliance */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">GST Number</label>
              <input
                type="text"
                name="gstNumber"
                value={formData.gstNumber}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2"
              />
            </div>

            <div>
              <label className="text-sm font-medium">PAN Number</label>
              <input
                type="text"
                name="panNumber"
                value={formData.panNumber}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">License Number</label>
            <input
              type="text"
              name="licenseNumber"
              value={formData.licenseNumber}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Certificate Expiry</label>
            <input
              type="date"
              name="certificateExpiry"
              value={formData.certificateExpiry}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Upload Certification</label>
            <input
              type="file"
              name="certificateFile"
              onChange={handleChange}
              className="w-full"
            />
          </div>

          {/* Contact */}
          <div>
            <label className="block text-sm font-medium mb-2">Contact Person</label>
            <input
              type="text"
              name="contactPerson"
              value={formData.contactPerson}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          {/* Email + phone */}
          <div className="grid grid-cols-2 gap-4">
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              className="border rounded-md px-3 py-2"
            />

            <input
              type="text"
              name="phone"
              placeholder="Phone"
              value={formData.phone}
              onChange={handleChange}
              className="border rounded-md px-3 py-2"
            />
          </div>

          <textarea
            name="address"
            placeholder="Address"
            value={formData.address}
            onChange={handleChange}
            className="w-full border rounded-md px-3 py-2"
          />

          {/* Contract */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label>Start</label>
              <input
                type="date"
                name="contractStart"
                value={formData.contractStart}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2"
              />
            </div>

            <div>
              <label>End</label>
              <input
                type="date"
                name="contractEnd"
                value={formData.contractEnd}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2"
              />
            </div>

            <div>
              <label>Days Left</label>
              <input
                type="text"
                value={formData.daysLeft}
                disabled
                className="w-full border rounded-md px-3 py-2 bg-gray-100"
              />
            </div>
          </div>

          {/* Rating */}
          <div>
            <label>Rating (1–5)</label>
            <input
              type="number"
              name="rating"
              min="0"
              max="5"
              value={formData.rating}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          <textarea
            name="performanceEvaluation"
            placeholder="Performance Notes"
            value={formData.performanceEvaluation}
            onChange={handleChange}
            className="w-full border rounded-md px-3 py-2"
          />

          {/* Status */}
          <div>
            <label>Status</label>
            <select
              name="vendorStatus"
              value={formData.vendorStatus}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Blacklisted">Blacklisted</option>
            </select>
          </div>

          {/* Project Assignment */}
          <div>
            <label>Assign Projects</label>
            <select
              name="assignedProjects"
              multiple
              value={formData.assignedProjects}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2 h-32"
            >
              {mockProjects.map((project, index) => (
                <option key={index} value={project}>
                  {project}
                </option>
              ))}
            </select>
          </div>

          {/* 🟢 4. RENDER CUSTOM FIELDS HERE */}
          <div className="pt-4 border-t border-gray-200">
              <CustomFieldRenderer 
                  module="vendors" 
                  customData={customData} 
                  onChange={(newData) => {
                      setCustomData(newData);
                      setIsDirty(true);
                  }} 
              />
          </div>

          <button
            type="submit"
            className="bg-gray-900 text-white px-6 py-2 rounded-md mt-4"
          >
            {editing ? "Update Vendor" : "Add Vendor"}
          </button>
        </form>
      </div>

      <NavigationBlocker isDirty={isDirty} />
    </>
  );
};

export default VendorForm;